/**
 * Memoize a value derived from an item, keyed by the item's identity.
 *
 * Sort comparators are why this exists. A sort runs its comparator ~n·log2(n)
 * times — ~178,000 for the 13,000-row order-edit table — but there are only n
 * distinct keys, and the accessors producing them are derived values
 * (reorderLabel, formatUnitsQuantity, four-level reaches into oi.product) far
 * more expensive than the comparison they feed. Caching per item turns hundreds
 * of thousands of accessor calls per sort into one per row.
 *
 * Entries are held weakly, so an item dropping out of the data takes its value
 * with it. The cache lives exactly as long as the returned function, so it can
 * never outlive the `derive` closure that filled it.
 *
 * Items are assumed immutable — a changed item arrives as a new object. That is
 * already what the sorted React lists assume about this data.
 *
 * Values are stored boxed rather than bare, so `null` and `undefined` cache like
 * any other value. A bare WeakMap can't tell "derived undefined" from "not
 * cached", and a column whose key is legitimately nullish — an unset Bin, a
 * product never received — would recompute on every one of a sort's ~n·log2(n)
 * comparisons, which is the exact cost this exists to remove. The box costs one
 * small object per item per cache, held weakly alongside the value it wraps.
 */
export function cacheByItem<T extends object, V>(derive: (item: T) => V): (item: T) => V {
  const cache = new WeakMap<T, { value: V }>();

  return (item) => {
    const cached = cache.get(item);
    if (cached !== undefined) {
      return cached.value;
    }

    const value = derive(item);
    cache.set(item, { value });

    return value;
  };
}
